import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Story {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  created_at: string;
}

const categoryColors: Record<string, string> = {
  "Field Stories": "bg-emerald-100 text-emerald-700",
  "Impact Reports": "bg-amber-100 text-amber-700",
  "Volunteer Voices": "bg-rose-100 text-rose-700",
  "News & Updates": "bg-sky-100 text-sky-700",
};

const categoryIcons: Record<string, string> = {
  "Field Stories": "ri-landscape-line",
  "Impact Reports": "ri-bar-chart-box-line",
  "Volunteer Voices": "ri-user-voice-line",
  "News & Updates": "ri-newspaper-line",
};

function getLocalizedField(story: Story, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (story as any)[field + suffix] || (story as any)[field + "_en"] || "";
}

export default function StoriesPreview() {
  const { t, i18n } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [stories, setStories] = useState<Story[]>([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [storiesError, setStoriesError] = useState<string | null>(null);

  useEffect(() => {
    async function loadStories() {
      try {
        const { data, error } = await supabase
          .from("stories")
          .select("*")
          .eq("published", true)
          .order("created_at", { ascending: false })
          .limit(3);

        if (error) {
          setStoriesError(error.message);
        } else {
          setStories((data as Story[]) ?? []);
        }
      } catch (err: any) {
        setStoriesError(err?.message || "Failed to load stories");
      } finally {
        setStoriesLoading(false);
      }
    }

    loadStories();
  }, []);

  useEffect(() => {
    if (storiesLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            // Add reveal-visible to all child cards too
            const children = Array.from(entry.target.children) as HTMLElement[];
            children.forEach((child) => child.classList.add("reveal-visible"));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 150}ms`;
      });
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, [storiesLoading, stories]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      i18n.language === "ps"
        ? "ps-AF"
        : i18n.language === "da"
        ? "fa-AF"
        : "en-US",
      { year: "numeric", month: "long", day: "numeric" }
    );
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#3B9EF5] mb-3 block">
            {t("storiesPreview.tag")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B2838] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("storiesPreview.title")}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            {t("storiesPreview.subtitle")}
          </p>
        </div>

        {storiesLoading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 text-sm mt-3">{t("common.loading")}</p>
          </div>
        ) : storiesError ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
              <i className="ri-error-warning-line text-2xl text-red-400"></i>
            </div>
            <p className="text-gray-700 font-medium mb-1">{t("storiesPage.errorTitle")}</p>
            <p className="text-gray-400 text-sm mb-5">{storiesError}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i> {t("storiesPage.retry")}
            </button>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-[#3B9EF5]/10">
              <i className="ri-book-open-line text-2xl text-[#3B9EF5]"></i>
            </div>
            <p className="text-gray-700 font-medium mb-1">{t("storiesPage.emptyTitle")}</p>
            <p className="text-gray-400 text-sm mb-5">{t("storiesPage.emptySubtitle")}</p>
            <Link
              to="/stories"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
            >
              {t("storiesPage.viewAll")} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10"
              ref={gridRef}
            >
              {stories.map((story) => (
                <Link
                  key={story.id}
                  to={`/stories/${story.slug}`}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3B9EF5]/40 transition-all duration-300 group flex flex-col cursor-pointer"
                >
                  <div className="relative h-48 sm:h-52 md:h-56 overflow-hidden flex-shrink-0">
                    <img
                      src={story.image}
                      alt={getLocalizedField(story, "title", i18n.language)}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${
                          categoryColors[story.category] || "bg-[#3B9EF5]/10 text-[#3B9EF5]"
                        }`}
                      >
                        <i
                          className={`${categoryIcons[story.category] || "ri-book-open-line"} text-[10px]`}
                        ></i>
                        {story.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(story.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {getLocalizedField(story, "read_time", i18n.language)}
                      </span>
                    </div>
                    <h3 className="font-bold text-[#1B2838] text-base md:text-lg mb-2 leading-tight group-hover:text-[#3B9EF5] transition-colors">
                      {getLocalizedField(story, "title", i18n.language)}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                      {getLocalizedField(story, "excerpt", i18n.language)}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                      <span className="text-xs text-gray-400">
                        {t("storiesPage.by")}{" "}
                        <strong className="text-[#1B2838]">
                          {getLocalizedField(story, "author", i18n.language)}
                        </strong>
                      </span>
                      <span className="text-[#3B9EF5] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                        {t("storiesPage.readMore")}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#1B2838] text-[#1B2838] rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#1B2838] hover:text-white transition-all"
              >
                {t("storiesPreview.viewAll")}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}